import React, { useEffect, useState } from 'react';
import { Dialog } from '@reach/dialog';
import { TextOnly } from './Text';
import { getToolRecoveryImage } from '../libs/db-lib';
import CopyLink from './CopyLink';
import { Loading } from './Loading';
import GenericPopover from './GenericPopover';
import LoaderButton from './LoaderButton';
import HelpTooltip from './Tooltips/HelpTooltip';
import { ToolRecoveryRequest } from '../types';

type ToolRecoveryModalProps = {
  showModal: boolean;
  handleCancel: () => void;
  toolRecoveryRequest: any; // This should contain { toolSerial, shopID, ownerShopID, docKey }
  toolRecoveryShops: any;
  contactOwner: (toolID: string) => Promise<void>;
  loadingActions: string;
  processRequest: (toolID: string, approved: boolean) => Promise<void>;
  handleShowRejectModal: () => void;
  setSelectedRequest: (selectedRequest: ToolRecoveryRequest) => void;
  setIsProcessingRequest: (isProcessingRequest: boolean) => void;
  setShowRejectReasonModal: (showRejectReasonModal: boolean) => void;
};

const ToolRecoveryModal = ({
  showModal,
  handleCancel,
  toolRecoveryRequest,
  toolRecoveryShops,
  contactOwner,
  loadingActions,
  processRequest,
  handleShowRejectModal,
  setSelectedRequest,
  setIsProcessingRequest,
  setShowRejectReasonModal,
}: ToolRecoveryModalProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [imageData, setImageData] = useState<Blob | undefined>();

  useEffect(() => {
    if (toolRecoveryRequest.docKey) {
      getImage();
    }
  }, [toolRecoveryRequest]);

  const getImage = async () => {
    setIsLoading(true)
    const imageDataBlob = await getToolRecoveryImage(toolRecoveryRequest.docKey);
    setImageData(imageDataBlob);
    setIsLoading(false);
  }

  function handleCancelModal() {
    document.querySelector('.c-modal')?.classList.add('closed');
    setTimeout(() => {
      handleCancel();
    }, 350);
  }

  return (
    <Dialog
      isOpen={showModal}
      onDismiss={handleCancelModal}
      className="c-modal large tool-recovery-modal"
      aria-label={'tool recovery modal'}
    >
      <button className="c-btn-icon c-modal__close" onClick={handleCancelModal}>
        <div className="c-btn__inner">
          <i className="c-btn__icon fa fa-times" />
        </div>
      </button>

      <div className="c-modal__body u-margin-top-large">

        {/* Tool Manufacturer */}
        <div>
          <span className='recovery-variable'>Tool Manufacturer </span>
          <span>{toolRecoveryRequest.toolManufacturer}</span>
        </div>

        {/* Tool Model */}
        <div>
          <span className='recovery-variable'>Tool Model </span>
          <span>{toolRecoveryRequest.toolModel}</span>
        </div>

        {/* Tool serial */}
        <div>
          <span className='recovery-variable'>Tool Serial</span>
          <CopyLink
            content={toolRecoveryRequest.toolSerial}
            className='u-font-mono'
            type={'TOOL SERIAL'}
            redirect={'/shopFunctions/toolLookup'}
            urlParams={`?query=${toolRecoveryRequest.toolSerial}&queryType=TOOL+SERIAL`}
            showCopy={false}
            divClass='inline-copylink'
          />
        </div>

        {/* Request shop */}
        <div>
        <span className='recovery-variable'>Requester Shop</span>
        <CopyLink
          content={toolRecoveryRequest.shopID}
          sub={toolRecoveryShops[toolRecoveryRequest.shopID]}
          className="u-font-mono"
          type="SHOP ID"
          redirect="/shopFunctions/getShopSummary"
          showCopy={false}
          urlParams={`?query=${toolRecoveryRequest.shopID}&queryType=SHOP+ID`}
          divClass='inline-copylink'
        />
        {
          toolRecoveryRequest.disputePaid ?
            <span className="u-text-success"> (Repaid Lost Dispute)</span>
          : toolRecoveryRequest.disputeInfoTransferred ?
            <span className="u-text-warning"> (Dispute Repayment Pending)</span>
          : ''
        }
        </div>

        {/* Tool owner shop */}
        <div>
          <span className='recovery-variable'>Tool Owner Shop </span>
          <CopyLink
            content={toolRecoveryRequest.ownerShopID}
            sub={toolRecoveryShops[toolRecoveryRequest.ownerShopID]}
            className="u-font-mono"
            type="SHOP ID"
            redirect="/shopFunctions/getShopSummary"
            showCopy={false}
            urlParams={`?query=${toolRecoveryRequest.ownerShopID}&queryType=SHOP+ID`}
            divClass='inline-copylink'
          />
          {
            toolRecoveryRequest.isEnterprise ?
              <span className="u-text-warning"> (Enterprise Shop)</span>
            : toolRecoveryRequest.disputePaid ?
              <span className="u-text-success"> (Dispute Resolved)</span>
            : toolRecoveryRequest.ownerShopWeLostDispute ?
              <span className="u-text-warning"> (Lost Dispute)</span>
            : ''
          }
        </div>

        {/* Serial detected */}
        <div>
          <span className='recovery-variable'>Serial Detected </span>

          {
            toolRecoveryRequest.serialDetected === true ?
            <HelpTooltip
              label={"Serial detected on image"}
              child={<span className="c-btn__icon fa fa-check icon-success" title="Serial detected on image" />}
              inline={true}
              additionalStyles={{zIndex: '11000'}}
            />:
            toolRecoveryRequest.serialDetected === false ?
            <HelpTooltip
              label={"Serial not detected on image"}
              child={<span className="c-btn__icon fa fa-question icon-warning" title="Serial not detected on image" />}
              inline={true}
              additionalStyles={{zIndex: '11000'}}
            />:
            null
          }
        </div>

        {toolRecoveryRequest?.status?.includes('CLOSED') ? (
          <div className="l-display-block">
            <div>
              { toolRecoveryRequest.status }
            </div>
            <div>
              { toolRecoveryRequest.closeReason }
            </div>
          </div>

        ) : toolRecoveryRequest?.status?.includes("PENDING") ? (
          <div className="l-inline-flex u-margin-top-small">
            {loadingActions !== toolRecoveryRequest.toolID ? (
              <>
                <GenericPopover
                  anchorElement={
                    <div className="icon-success icon-clickable u-margin-right-small c-btn__inner">
                      <span
                        className="fa fa-check"
                        title="Approve Request"
                      />
                      <span className='u-margin-left-small'>Approve Request</span>
                    </div>
                  }
                  body={
                    <strong>
                      Are you sure you want to approve this request?
                    </strong>
                  }
                  primaryBtnElement={
                    <LoaderButton
                      className="c-btn-medium c-btn-success"
                      disabled={false}
                      text="Yes, approve it"
                      loadingText="Approving..."
                      icon={'fa fa-check'}
                    />
                  }
                  secondaryBtnElement={
                    <button className="c-btn-medium c-btn-outline">
                      <div className="c-btn__inner">
                        <i className="c-btn__icon fal fa-times" />
                        No, not yet
                      </div>
                    </button>
                  }
                  primaryAction={ async () => {
                    return processRequest(toolRecoveryRequest.toolID, true);
                  }}
                  color="#0088cc"
                  position="top"
                  align="end"
                  arrowSize={5}
                  closeOnClick={false}
                />

                <GenericPopover
                  anchorElement={
                    <div className="icon-red icon-clickable u-margin-right-small c-btn__inner">
                      <span
                        className="fa fa-check"
                        title="Deny Request"
                      />
                      <span className='u-margin-left-small'>Deny Request</span>
                    </div>
                  }
                  body={
                    <strong>
                      Are you sure you want to deny this request?
                    </strong>
                  }
                  primaryBtnElement={
                    <LoaderButton
                      className="c-btn-medium c-btn-error"
                      disabled={false}
                      text="Yes, deny it"
                      loadingText="Denying..."
                      icon={'fa fa-check'}
                    />
                  }
                  secondaryBtnElement={
                    <button className="c-btn-medium c-btn-outline">
                      <div className="c-btn__inner">
                        <i className="c-btn__icon fal fa-times" />
                        No, not yet
                      </div>
                    </button>
                  }
                  primaryAction={ async () => {
                    setSelectedRequest(toolRecoveryRequest);
                    setIsProcessingRequest(true);
                    setShowRejectReasonModal(true);
                  }}
                  color="#0088cc"
                  position="top"
                  align="end"
                  arrowSize={5}
                  closeOnClick={false}
                />
              </>
            ) : (
              <button className="c-btn-icon">
                <div className="c-btn__inner">
                  <i className="c-btn__icon fal fa-spinner-third spinning" />
                </div>
              </button>
            )}
          </div>
        ) : loadingActions !== toolRecoveryRequest.toolID ? (
        <div className='u-margin-top-small'>
          <div className="l-inline-flex">
            <GenericPopover
              anchorElement={
                <div className="clickable u-margin-right-small c-btn__inner">
                    <span
                      className="fa fa-envelope"
                      title="Contact Owner"
                    />
                    <span className='u-margin-left-small'>Contact Owner</span>
                </div>
              }
              body={
                <strong>
                  Are you sure you want to open a ticket with this
                  tool's owner?
                </strong>
              }
              primaryBtnElement={
                <LoaderButton
                  className="c-btn-medium c-btn-success"
                  disabled={false}
                  text="Yes, open it"
                  loadingText="Opening..."
                  icon={'fa fa-check'}
                />
              }
              secondaryBtnElement={
                <button className="c-btn-medium c-btn-outline">
                  <div className="c-btn__inner">
                    <i className="c-btn__icon fal fa-times" />
                    No, not yet
                  </div>
                </button>
              }
              primaryAction={ async () => {
                return contactOwner(toolRecoveryRequest.toolID);
              }}
              color="#0088cc"
              position="top"
              align="end"
              arrowSize={5}
              closeOnClick={false}
            />

            <GenericPopover
              anchorElement={
                <div className="clickable u-margin-left-small u-margin-right-small c-btn__inner">
                    <span
                      className="fa fa-trash-alt"
                      title="Close Request"
                    />
                    <span className='u-margin-left-small'>Close Request</span>
                </div>
              }
              body={
                <strong>
                  Are you sure you want to close this request?
                </strong>
              }
              primaryBtnElement={
                <LoaderButton
                  className="c-btn-medium c-btn-success"
                  disabled={false}
                  text="Yes, close it"
                  loadingText="Closing..."
                  icon={'fa fa-check'}
                />
              }
              secondaryBtnElement={
                <button className="c-btn-medium c-btn-outline">
                  <div className="c-btn__inner">
                    <i className="c-btn__icon fal fa-times" />
                    No, not yet
                  </div>
                </button>
              }
              primaryAction={ async () => {return handleShowRejectModal()}}
              color="#0088cc"
              position="top"
              align="end"
              arrowSize={5}
              closeOnClick={false}
            />
          </div>

          </div>
        ) : (
          <button className="c-btn-icon">
            <div className="c-btn__inner">
              <i className="c-btn__icon fal fa-spinner-third spinning" />
            </div>
          </button>
        )}

        <div>
          {isLoading ?
          <div className='loading-container'><Loading className='spinner'/></div>:
          <div className='tool-image-container u-margin-top-large'>
            <img src={imageData ? URL.createObjectURL(imageData) : ''} alt={'ISSimage'} className='tool-image'/>
          </div>
          }

        </div>

        <div className="l-flex-between">
          <div />
          <button
            className="c-btn-outline u-margin-top-large"
            onClick={handleCancelModal}
          >
            {TextOnly('dismiss')}
          </button>
        </div>


      </div>
    </Dialog>
  );
};

export default ToolRecoveryModal;
